import { analyticsConstant } from 'types/analytics';
import { ClassType } from 'types/cover';

import { PolicyResponse, Risk } from '../../../types/policy';

const { NOT_SET } = analyticsConstant;

const appPartner = window.qbeTrack?.meta?.appPartner ?? undefined;

type BuilderConfig = {
  brandKey: string;
  categoryKey: string;
  variantKey: string;
  nameKey: string;
  priceKey: string;
};

const buildEntities = (
  risks: Risk[],
  industry: string,
  paymentFrequency: string,
  config: BuilderConfig
) => {
  return risks.flatMap((element) => {
    const premiumClasses = element?.premium?.premiumClass;
    if (!premiumClasses) return [];

    const classes =
      premiumClasses.length < 3
        ? [premiumClasses[0]]
        : premiumClasses.filter((subElement) => subElement.classType !== ('TOT' as ClassType));

    return classes.map((c) => ({
      [config.brandKey]: appPartner,
      [config.categoryKey]: industry,
      [config.variantKey]: paymentFrequency,
      quantity: 1,
      [config.nameKey]: c.classType || NOT_SET,
      [config.priceKey]: c.transactionPremium?.premium || NOT_SET,
    }));
  });
};

const buildItems = (risks: Risk[], industry: string, paymentFrequency: string) =>
  buildEntities(risks, industry, paymentFrequency, {
    brandKey: 'item_brand',
    categoryKey: 'item_category',
    variantKey: 'item_variant',
    nameKey: 'item_name',
    priceKey: 'price',
  });

const buildProducts = (risks: Risk[], industry: string, paymentFrequency: string) =>
  buildEntities(risks, industry, paymentFrequency, {
    brandKey: 'brand',
    categoryKey: 'category',
    variantKey: 'variant',
    nameKey: 'name',
    priceKey: 'price',
  });

const buildRevenue = (
  policyNumber: string,
  premiums: PolicyResponse,
  industry: string,
  gaVersion: string,
  paymentFrequency: string
) => {
  const premium = premiums?.policy?.policyPremium?.transactionPremium?.premium;

  const risks = premiums?.policy?.risks;

  if (!premium || !risks) return false;

  if (gaVersion === 'UA') {
    return {
      purchase: {
        actionField: {
          id: policyNumber,
          affiliation: appPartner,
          revenue: premium,
        },
        products: buildProducts(risks, industry, paymentFrequency),
      },
    };
  }

  // GA4 Ecommerce
  return buildItems(risks, industry, paymentFrequency);
};

/**
 * Annual Payment E-Commerce Logic.
 */
export const buildAnnualRevenue = (
  policyNumber: string,
  premiums: PolicyResponse,
  industry: string,
  gaVersion: string
) => buildRevenue(policyNumber, premiums, industry, gaVersion, 'oneOff');

/**
 * Monthly Payment E-Commerce Logic.
 * TODO Complete the monthly payment once the payment api ready.
 */
export const buildMonthlyRevenue = (
  policyNumber: string,
  premiums: PolicyResponse,
  industry: string,
  gaVersion: string
) => buildRevenue(policyNumber, premiums, industry, gaVersion, 'monthly');

// Backward compatibility exports
export const getAnnualRevenue = buildAnnualRevenue;
export const getMonthlyRevenue = buildMonthlyRevenue;
